import UiLanguage from "@ext/localization/core/model/Language";
import { getCurrentLanguage } from "@ext/localization/locale/translate";
import toolsEn from "../locale/en";
import toolsRu from "../locale/ru";

const ru = {
	loading: "Загрузка общих материалов",
	"update-failed": "Не удалось обновить общие материалы",
	"update-failed.network": "Нет связи с сервером.",
	"update-failed.no-access": "Нет доступа к репозиторию.",
	"update-failed.other": "Обновление не удалось.",
	"update-failed.shown": "Показаны последние загруженные версии.",
	"update-failed.action.network":
		"Проверьте подключение к интернету. Обновление повторится при следующей загрузке страницы.",
	"update-failed.action.no-access":
		"Обратитесь к администратору пространства, чтобы получить доступ к репозиторию общих материалов.",
	"update-failed.action.other":
		"Обновление повторится при следующей загрузке страницы. Если ошибка остается, сообщите администратору пространства.",
	"error.title": "Не удалось загрузить общие материалы",
	"error.close": "Закрыть",
	"error.goto": "Открыть общие материалы",
	"reload-refused.title": "Не удалось загрузить общие материалы заново",
	"reload-refused.network":
		"Нет связи с сервером, поэтому общие материалы не загружены заново. Неопубликованные изменения сохранены. Проверьте соединение и попробуйте еще раз.",
	reload: "Загрузить заново",
	"reload.title": "Загрузить общие материалы заново?",
	"reload.description": "Неопубликованные изменения ({count}) будут потеряны.",
	"reload.description-uncounted":
		"Не удалось посчитать неопубликованные изменения. Если они есть, они будут потеряны.",
	"reload.cancel": "Отмена",
	"reload.in-progress": "Загрузка общих материалов заново…",
	"scope.catalog": "Каталог",
	"scope.workspace": "Пространство",
	"empty.fragment": "В пространстве пока нет фрагментов",
	"empty.template": "В пространстве пока нет шаблонов",
	"empty-hint.fragment": "Добавьте фрагменты в общие материалы, и они появятся во всех каталогах пространства",
	"empty-hint.template": "Добавьте шаблоны в общие материалы, и они появятся во всех каталогах пространства",
	"none.fragment": "Нет фрагментов",
	"none.template": "Нет шаблонов",
	"unavailable.failed": "Не удалось загрузить общие материалы",
	"unavailable.absent": "Общие материалы еще не загружены",
	untitled: "Без названия",
	"no-content": "Нет содержимого",
	"disabled-empty.fragment": "Нельзя вставить пустой фрагмент",
	"disabled-empty.template": "Нельзя выбрать пустой шаблон",
	"unpublished-label": "Неопубликованные изменения",
	unpublished:
		"В общих материалах есть неопубликованные изменения во фрагментах или шаблонах. Вы видите их во всех каталогах, другие пользователи — нет. Опубликуйте их, чтобы изменения увидели все.",
	open: toolsRu["catalog-title"],
	"open.not-loaded": "Общие материалы еще не загружены. Попробуйте позже.",
	"open.hint": "Фрагменты и шаблоны, общие для всех каталогов пространства",
};

type UiKey = keyof typeof ru;

const en: Record<UiKey, string> = {
	loading: "Loading shared materials",
	"update-failed": "Could not update the shared materials",
	"update-failed.network": "The server is unreachable.",
	"update-failed.no-access": "No access to the repository.",
	"update-failed.other": "The update failed.",
	"update-failed.shown": "The last downloaded versions are shown.",
	"update-failed.action.network":
		"Check your internet connection. The update is retried the next time the page loads.",
	"update-failed.action.no-access":
		"Ask the workspace administrator to give you access to the shared materials repository.",
	"update-failed.action.other":
		"The update is retried the next time the page loads. If the error stays, tell the workspace administrator.",
	"error.title": "Failed to load shared materials",
	"error.close": "Close",
	"error.goto": "Open shared materials",
	"reload-refused.title": "Could not reload the shared materials",
	"reload-refused.network":
		"The server is unreachable, so the shared materials were not reloaded. Unpublished changes are kept. Check the connection and try again.",
	reload: "Reload",
	"reload.title": "Reload the shared materials?",
	"reload.description": "Unpublished changes ({count}) will be lost.",
	"reload.description-uncounted": "Could not count unpublished changes. If there are any, they will be lost.",
	"reload.cancel": "Cancel",
	"reload.in-progress": "Reloading the shared materials…",
	"scope.catalog": "Catalog",
	"scope.workspace": "Workspace",
	"empty.fragment": "The workspace has no fragments yet",
	"empty.template": "The workspace has no templates yet",
	"empty-hint.fragment": "Add fragments to the shared materials, and they appear in every catalog of the workspace",
	"empty-hint.template": "Add templates to the shared materials, and they appear in every catalog of the workspace",
	"none.fragment": "No fragments",
	"none.template": "No templates",
	"unavailable.failed": "Failed to load shared materials",
	"unavailable.absent": "Shared materials are not loaded yet",
	untitled: "Untitled",
	"no-content": "No content",
	"disabled-empty.fragment": "An empty fragment cannot be inserted",
	"disabled-empty.template": "An empty template cannot be chosen",
	"unpublished-label": "Unpublished changes",
	unpublished:
		"The shared materials have unpublished changes in fragments or templates. You see them in every catalog, other users do not. Publish them so that everyone sees the changes.",
	open: toolsEn["catalog-title"],
	"open.not-loaded": "Shared materials are not loaded yet. Try again later.",
	"open.hint": "Fragments and templates shared by every catalog in the workspace",
};

export const tui = (key: UiKey, params: Record<string, string | number> = {}): string => {
	const text = (getCurrentLanguage() === UiLanguage.ru ? ru : en)[key];
	return Object.entries(params).reduce((result, [name, value]) => result.replace(`{${name}}`, String(value)), text);
};
